/**
 * @file professional.js
 *
 * Professional API Service
 *
 * Thin wrapper around axios calls to /api/v1/professional.
 * Each method maps 1:1 to a backend route.
 * The shared `api` instance handles: auth headers, token refresh, error toasts.
 *
 * Pages never call these directly — they use hooks (useProfessionals.js) instead.
 */
import api from "./api";

export const professionalService = {
  /** List all professionals. */
  getAll: () => api.get("/professional/list"),
  /** View one professional's full profile by ID. */
  getById: (id) => api.get(`/professional/${id}`),
  /** Get the logged-in professional's own profile. */
  getMe: () => api.get("/professional/me"),
  /** Update the logged-in professional's profile (bio, price, etc.). */
  updateMe: (data) => api.put("/professional/me", data),
  /** List patients associated with a specific professional. */
  getPatients: (id) => api.get(`/professional/${id}/patients`),
  /** Update the professional's public URL slug. */
  updateSlug: (slug) => api.put("/professional/me/slug", { slug }),
  /** Update a consultation service by ID. */
  updateService: (id, data) => api.put(`/professional/me/services/${id}`, data),
  /** Update a location by ID. */
  updateLocation: (id, data) => api.put(`/professional/me/locations/${id}`, data),
  /** Fetch the professional's specialty list. */
  getSpecialties: () => api.get("/professional/me/specialties"),
  /** Add a specialty to the professional's profile. */
  addSpecialty: (data) => api.post("/professional/me/specialties", data),
  /** Update an existing specialty. */
  updateSpecialty: (id, data) => api.put(`/professional/me/specialties/${id}`, data),
  /** Remove a specialty. */
  removeSpecialty: (id) => api.delete(`/professional/me/specialties/${id}`),
  /** Upload a profile photo. Content-Type is set to undefined so axios auto-detects multipart boundary. */
  uploadPhoto: (formData) => api.post("/professional/me/photo", formData, {
    headers: { "Content-Type": undefined },
  }),
  /** Delete the professional's profile photo. */
  deletePhoto: () => api.delete("/professional/me/photo"),
  /** Save fiscal data for invoicing (CFDI). */
  saveFiscalData: (data) => api.put("/professional/me/fiscal-data", data),
  /** Upload Constancia de Situación Fiscal (PDF). */
  uploadConstancia: (formData) => api.post("/professional/me/constancia", formData, {
    headers: { "Content-Type": undefined },
  }),
};
